import React, { useEffect, useState, useRef, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, CheckCircle2, XCircle, ArrowRight, ChevronLeft, User, RotateCcw, ExternalLink, ChevronDown, MousePointerClick, Lightbulb, X, Info, Globe, Lock, BarChart3, Users, Target, ArrowUpRight, Eye, Heart, GraduationCap, UserCheck, LayoutGrid, Download } from 'lucide-react';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { useScreenInit } from './useScreenInit.js';
const HAND_IMAGE_A = 'https://cdn.magicpatterns.com/uploads/q68S7twZc7G7SaTdJTxMZz/Screenshot_2026-07-07_at_21.18.53.png';
const HAND_IMAGE_B = 'https://cdn.magicpatterns.com/uploads/xgpr5FDELYM9QBcjf9ZqrT/Screenshot_2026-07-07_at_21.18.28.png';
// Human / lifestyle moments used to add emotional context across the teaser.
const LIFE_SCRATCH = 'https://cdn.magicpatterns.com/uploads/vWQyas57qacd3QUAvmi73u/Screenshot_2026-07-07_at_21.15.35.png';
const LIFE_PIANO = 'https://cdn.magicpatterns.com/uploads/gsRJTf4PjatDFzVBKGbw4N/Screenshot_2026-07-07_at_21.14.44.png';
const LIFE_HOLDING = 'https://cdn.magicpatterns.com/uploads/oHanXBcYppgZvm79AsvaFq/Screenshot_2026-07-07_at_21.14.21.png';
// Prevalence / data screen image.
const DATA_HANDS = 'https://cdn.magicpatterns.com/uploads/712ctsaAYJRoe7FAWDS5UL/Screenshot_2026-07-07_at_21.59.43.png';
// Patient profile portraits used on the flip-card backs.
const PATIENT_A_PORTRAIT = 'https://cdn.magicpatterns.com/uploads/kPvoMAxGz88smhFVreDFYC/Screenshot_2026-07-07_at_23.11.20.png';
const PATIENT_B_PORTRAIT = 'https://cdn.magicpatterns.com/uploads/qZcDW8XjYmaVPXXJvs8pZW/Screenshot_2026-07-07_at_23.11.33.png';
const ULogo = ({
  className = 'w-6 h-6'
}: {
  className?: string;
}) => <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-0">
    <path d="M4 4V12C4 16.4183 7.58172 20 12 20C16.4183 20 20 16.4183 20 12V4" stroke="currentColor" strokeWidth="4" strokeLinecap="round" data-id="element-1" />
  </svg>;
// --- "Behind the design" strategy layer ---------------------------------
// A quiet, expert-only annotation of the persuasion strategy behind each
// screen. Grounded in Michael Pantalon's (Yale) "Instant Influence" method,
// itself rooted in motivational interviewing: the HCP articulates their own
// reasoning rather than being pitched to. Each screen maps to a step in that
// sequence; the panel jumps straight to the note that fits the current screen.
type StrategyNote = {
  tag: string;
  title: string;
  question?: string;
  body: string;
};
// Indexed to match the `steps` array order below.
const STRATEGY_NOTES: StrategyNote[] = [{
  tag: 'The Hook',
  title: 'A challenge, not a claim',
  body: 'We open with a puzzle the eye cannot solve: two patients who look identical today. This creates awareness that chronicity is almost impossible to judge visually — the HCP feels the diagnostic gap themselves instead of being told about it. Engagement starts as curiosity, not a sales message.'
}, {
  tag: 'Question 1 · Own reasons',
  title: 'Let them name the problem',
  question: '“Why might you want to look closer at this?”',
  body: 'Rather than listing why chronicity matters, we surface the everyday tells the HCP already recognises. People trust and defend their own reasons far more than an external pitch — so we get them stating the case for change in their own words.'
}, {
  tag: 'Question 2 · Self-assessment',
  title: 'Force a concrete number',
  question: '“On a scale of 1–10, how confident are you?”',
  body: 'A slider turns a vague feeling into a specific self-rating. The number is the HCP’s own — which makes the next step land. It also quietly captures readiness as behavioural data rather than a simple click.'
}, {
  tag: 'Question 3 · The key move',
  title: 'Why not a lower number?',
  question: '“Why didn’t you pick something lower?”',
  body: 'This is the pivotal Instant Influence question. Asking why the number isn’t lower pulls out positive motivation — the HCP argues for the change instead of against it. Self-generated reasons are the strongest driver of a shift in behaviour.'
}, {
  tag: 'Reframe · In their words',
  title: 'Redefine chronicity',
  body: 'Having heard their own reasoning, we reframe CHE by duration and recurrence rather than appearance — future-pacing what changes when chronicity is uncovered. It reads as a shared insight, not a lecture.'
}, {
  tag: 'Question 5 · Why it matters',
  title: 'Make the relevance personal',
  body: 'Prevalence data answers “why does this matter to my practice?”. Framing it as “more common than it looks” deepens the emotional and clinical relevance the HCP has already started to feel — the stat confirms their instinct rather than replacing it.'
}, {
  tag: 'The Solution',
  title: 'Arrive, don’t instruct',
  body: 'Only now — after the HCP has done the thinking — do we bridge to ANZUPGO. Because they reached the need themselves, the treatment reads as the logical answer to their own conclusion, never as something pushed on them.'
}, {
  tag: 'Question 7 · Small step',
  title: 'A low-resistance next action',
  body: 'We close with one small, easy step — a summary to download or a chat to book — rather than a hard ask. Low-resistance actions convert far better and keep the whole experience consultative instead of promotional.'
}];
const METHOD_SOURCE = 'Michael Pantalon, Yale · “Instant Influence” — rooted in motivational interviewing.';
// The full Instant Influence methodology write-up. Used both as the strategy
// theory copy and as the storyboard's opening intro (on screen + in the PDF).
const INSTANT_INFLUENCE_THEORY = {
  title: 'Instant Influence',
  source: 'Michael Pantalon, PhD · Yale University',
  paragraphs: ['This teaser is built on Instant Influence, a method developed by Yale researcher Michael Pantalon and rooted in motivational interviewing. It flips traditional persuasion on its head: instead of listing benefits or pushing a claim, you guide people to voice their own reasons for change through a short, deliberate sequence of questions.', 'The pivotal move is asking why someone didn’t pick a lower number. Where “why not higher?” invites resistance, “why not lower?” pulls out positive motivation — the person ends up arguing for the change themselves. Self-generated reasons are the single strongest driver of a genuine shift in behaviour.', 'It works because people trust and defend their own conclusions far more than any external pitch. Applied here, it lets the HCP reach the clinical need for chronic hand eczema themselves — the experience stays engaging, consultative, and never patronising, and ANZUPGO arrives as the logical answer to a conclusion they reached on their own.']
};
function StrategyPanel({
  open,
  onClose,
  activeIndex
}: {
  open: boolean;
  onClose: () => void;
  activeIndex: number;
}) {
  const activeRef = useRef<HTMLDivElement | null>(null);
  const [showTheory, setShowTheory] = useState(false);
  useEffect(() => {
    if (open && activeRef.current) {
      activeRef.current.scrollIntoView({
        block: 'start',
        behavior: 'auto'
      });
    }
  }, [open, activeIndex]);
  useEffect(() => {
    if (!open) setShowTheory(false);
  }, [open]);
  return <AnimatePresence data-id="element-2">
      {open && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-black/60 backdrop-blur-sm z-[60]" data-id="element-3" />
          <motion.div role="dialog" aria-modal="true" aria-label="The strategy behind this screen" initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 300
      }} className="absolute bottom-0 left-0 right-0 z-[61] max-h-[82%] flex flex-col bg-card-bg border-t border-primary/40 rounded-t-3xl shadow-2xl" data-id="element-4">
            <div className="shrink-0 px-6 pt-5 pb-4 border-b border-surface flex items-start justify-between gap-4" data-id="element-5">
              <div className="flex items-center gap-2 text-primary" data-id="element-6">
                <Lightbulb size={18} data-id="element-7" />
                <span className="text-xs font-bold uppercase tracking-widest" data-id="element-8">
                  Behind the design
                </span>
              </div>
              <button onClick={onClose} aria-label="Close" className="w-8 h-8 -mt-1 -mr-1 rounded-full flex items-center justify-center text-text-secondary hover:text-text-primary hover:bg-surface transition-colors" data-id="element-9">
                <X size={18} data-id="element-10" />
              </button>
            </div>
            <div className="overflow-y-auto hide-scrollbar px-6 py-5 space-y-3" data-id="element-11">
              <p className="text-[11px] text-text-secondary/70 leading-relaxed mb-1" data-id="element-12">
                Every screen is a step in a self-persuasion sequence — the HCP
                reveals their own thinking and reaches the right conclusion,
                without being educated at or sold to.
              </p>
              {STRATEGY_NOTES.map((note, i) => {
            const isActive = i === activeIndex;
            return <div key={note.title} ref={isActive ? activeRef : undefined} className={`rounded-2xl border p-4 transition-colors scroll-mt-2 ${isActive ? 'border-primary bg-primary/10' : 'border-surface bg-secondary/40 opacity-60'}`} data-id="element-13">
                    <div className="flex items-center gap-2 mb-1.5" data-id="element-14">
                      <span className={`text-[10px] font-bold uppercase tracking-widest ${isActive ? 'text-primary' : 'text-text-secondary'}`} data-id="element-15">
                        {note.tag}
                      </span>
                      {isActive && <span className="text-[9px] font-bold uppercase tracking-wider bg-primary text-secondary px-2 py-0.5 rounded-full" data-id="element-16">
                          This screen
                        </span>}
                    </div>
                    <p className="text-sm font-bold text-text-primary leading-snug" data-id="element-17">
                      {note.title}
                    </p>
                    {note.question && <p className="text-[13px] italic text-primary/90 mt-1" data-id="element-18">
                        {note.question}
                      </p>}
                    <p className="text-[13px] text-text-secondary leading-relaxed mt-1.5" data-id="element-19">
                      {note.body}
                    </p>
                  </div>;
          })}
              <button onClick={() => setShowTheory(true)} className="w-full text-left flex items-center gap-2 pt-1 text-[10px] text-text-secondary/60 hover:text-primary transition-colors group" data-id="element-20">
                <Info size={12} className="shrink-0 text-text-secondary/50 group-hover:text-primary transition-colors" data-id="element-21" />
                <span className="leading-relaxed underline decoration-dotted underline-offset-2" data-id="element-22">
                  {METHOD_SOURCE}
                </span>
              </button>
            </div>

            {/* Theory pop-up — a short primer on Pantalon's approach */}
            <AnimatePresence data-id="element-23">
              {showTheory && <>
                  <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} onClick={() => setShowTheory(false)} className="absolute inset-0 bg-black/50 z-[62] rounded-t-3xl" data-id="element-24" />
                  <motion.div role="dialog" aria-modal="true" aria-label="About Michael Pantalon's Instant Influence" initial={{
              opacity: 0,
              scale: 0.94,
              y: 12
            }} animate={{
              opacity: 1,
              scale: 1,
              y: 0
            }} exit={{
              opacity: 0,
              scale: 0.94,
              y: 12
            }} transition={{
              type: 'spring',
              damping: 26,
              stiffness: 320
            }} className="absolute z-[63] left-4 right-4 bottom-4 bg-secondary border border-primary/40 rounded-2xl shadow-2xl p-5" data-id="element-25">
                    <div className="flex items-start justify-between gap-3 mb-2" data-id="element-26">
                      <div className="flex items-center gap-2 text-primary" data-id="element-27">
                        <Info size={16} data-id="element-28" />
                        <span className="text-xs font-bold uppercase tracking-widest" data-id="element-29">
                          The theory
                        </span>
                      </div>
                      <button onClick={() => setShowTheory(false)} aria-label="Close" className="w-7 h-7 -mt-0.5 -mr-0.5 rounded-full flex items-center justify-center text-text-secondary hover:text-text-primary hover:bg-card-bg transition-colors" data-id="element-30">
                        <X size={16} data-id="element-31" />
                      </button>
                    </div>
                    <p className="text-sm font-bold text-text-primary leading-snug mb-2" data-id="element-32">
                      Instant Influence · Michael Pantalon, Yale
                    </p>
                    <p className="text-[13px] text-text-secondary leading-relaxed" data-id="element-33">
                      A method rooted in motivational interviewing that flips
                      traditional persuasion on its head. Instead of listing
                      benefits, you guide people to voice their{' '}
                      <span className="text-text-primary font-semibold" data-id="element-34">
                        own reasons
                      </span>{' '}
                      for change through a short sequence of questions — most
                      importantly “why not a lower number?”, which draws out
                      positive motivation.
                    </p>
                    <p className="text-[13px] text-text-secondary leading-relaxed mt-2" data-id="element-35">
                      It works because people trust and defend their own
                      conclusions far more than an external pitch. Here it lets
                      the HCP reach the clinical need themselves — engaging,
                      consultative, and never patronising.
                    </p>
                  </motion.div>
                </>}
            </AnimatePresence>
          </motion.div>
        </>}
    </AnimatePresence>;
}
export function App() {
  const init = useScreenInit();
  const [showStrategy, setShowStrategy] = useState(false);
  // Flow: clickable banner hook -> teaser.
  // Deep-link map: step 0 = banner, step 1+ = teaser.
  const initStep = typeof init.step === 'number' ? init.step : 0;
  // The banner hook is a standalone, clickable ad; the teaser begins on tap.
  const [launched, setLaunched] = useState<boolean>(initStep >= 1);
  // anzupgo.com opens embedded inside the Snak, not as an external jump.
  const [showSite, setShowSite] = useState(false);
  // Behind-the-scenes data-knowledge view for the Snak owner.
  const [showData, setShowData] = useState(false);
  // Traditional storyboard / review view with per-page comments.
  const [showStoryboard, setShowStoryboard] = useState(false);
  const [currentStep, setCurrentStep] = useState<number>(initStep >= 1 ? initStep - 1 : 0);
  const nextStep = () => setCurrentStep(prev => Math.min(steps.length - 1, prev + 1));
  // Stepping back from the first teaser screen returns to the banner hook.
  const prevStep = () => {
    if (currentStep === 0) {
      setLaunched(false);
    } else {
      setCurrentStep(prev => Math.max(0, prev - 1));
    }
  };
  const steps = [<FlipRevealScreen key="flip" data-id="element-36" />, <ProblemMirrorScreen key="problem" data-id="element-37" />, <SelfRatingScreen key="rating" data-id="element-38" />, <SelfPersuasionScreen key="persuasion" data-id="element-39" />, <DefinitionScreen key="definition" data-id="element-40" />, <DataScreen key="data" data-id="element-41" />, <BrandBridgeScreen key="brand" data-id="element-42" />, <CTAScreen key="cta" onReplay={() => setCurrentStep(0)} onExplore={() => setShowSite(true)} data-id="element-43" />];
  const progressPercentage = (currentStep + 1) / steps.length * 100;
  if (!launched) {
    return <BannerAd onLaunch={() => setLaunched(true)} data-id="element-44" />;
  }
  if (showSite) {
    return <SiteEmbedScreen onBack={() => setShowSite(false)} data-id="element-45" />;
  }
  if (showData) {
    return <DataInsightsScreen onBack={() => setShowData(false)} data-id="element-46" />;
  }
  if (showStoryboard) {
    return <StoryboardScreen onBack={() => setShowStoryboard(false)} data-id="element-47" />;
  }
  return <div className="flex w-full min-h-screen bg-white text-text-primary font-sans overflow-hidden relative selection:bg-primary/30 justify-center sm:py-6" data-id="element-48">
      <div className="w-full max-w-md mx-auto flex flex-col relative z-10 min-h-[100dvh] sm:min-h-0 sm:h-[calc(100dvh-3rem)] bg-secondary anzupgo-waves overflow-hidden sm:rounded-[2rem] shadow-2xl" data-id="element-49">
        {/* Top Progress Bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-surface z-50" data-id="element-50">
          <motion.div className="h-full bg-primary" initial={{
          width: 0
        }} animate={{
          width: `${progressPercentage}%`
        }} transition={{
          duration: 0.3
        }} data-id="element-51" />
        </div>
        {/* Persistent Header */}
        <div className="w-full flex justify-between items-center px-6 py-6 z-20 shrink-0" data-id="element-52">
          <div className="flex items-center gap-3" data-id="element-53">
            <div className="flex items-center gap-1.5 text-primary" data-id="element-54">
              <ULogo className="w-5 h-5" data-id="element-55" />
              <span className="text-lg font-bold tracking-tight text-text-primary flex items-center gap-1" data-id="element-56">
                Anzupgo<sup className="text-[10px] mt-1" data-id="element-57">®</sup>
                <ChevronDown size={12} className="text-text-secondary ml-0.5" data-id="element-58" />
              </span>
            </div>
            <div className="w-px h-6 bg-text-secondary/20" data-id="element-59" />
            <div className="flex flex-col items-center justify-center opacity-60" data-id="element-60">
              <span className="text-[8px] font-bold tracking-[0.2em] text-text-secondary" data-id="element-61">
                LEO
              </span>
            </div>
          </div>

          {/* Quiet maker-only triggers: storyboard + insights + strategy note */}
          <div className="flex items-center gap-2" data-id="element-62">
            <button onClick={() => setShowStoryboard(true)} aria-label="Open the review storyboard" title="Storyboard" className="w-8 h-8 rounded-full border border-text-secondary/25 flex items-center justify-center text-text-secondary/50 hover:text-primary hover:border-primary/50 transition-colors" data-id="element-63">
              <LayoutGrid size={15} data-id="element-64" />
            </button>
            <button onClick={() => setShowData(true)} aria-label="Show what data this Snak reveals" title="Snak insights" className="w-8 h-8 rounded-full border border-text-secondary/25 flex items-center justify-center text-text-secondary/50 hover:text-primary hover:border-primary/50 transition-colors" data-id="element-65">
              <BarChart3 size={15} data-id="element-66" />
            </button>
            <button onClick={() => setShowStrategy(true)} aria-label="Show the strategy behind this screen" title="Behind the design" className="w-8 h-8 rounded-full border border-text-secondary/25 flex items-center justify-center text-text-secondary/50 hover:text-primary hover:border-primary/50 transition-colors" data-id="element-67">
              <Lightbulb size={15} data-id="element-68" />
            </button>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col relative px-6 pb-32 overflow-y-auto hide-scrollbar" data-id="element-69">
          <AnimatePresence mode="wait" data-id="element-70">{steps[currentStep]}</AnimatePresence>
        </div>

        {/* Global Navigation Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-6 flex justify-between items-center z-20 bg-gradient-to-t from-secondary via-secondary to-transparent pt-12" data-id="element-71">
          <div className="w-16" data-id="element-72">
            <button onClick={prevStep} aria-label={currentStep === 0 ? 'Back to banner' : 'Previous'} className="w-12 h-12 rounded-full border border-text-secondary/40 flex items-center justify-center text-text-secondary hover:text-text-primary hover:border-text-secondary transition-all" data-id="element-73">
              <ChevronLeft size={24} data-id="element-74" />
            </button>
          </div>

          <div className="flex flex-col items-center gap-2" data-id="element-75">
            <div className="flex items-center gap-2" data-id="element-76">
              {steps.map((_, i) => <div key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i === currentStep ? 'bg-primary w-6' : 'bg-text-secondary/30 w-1.5'}`} data-id="element-77" />)}
            </div>
            <span className="text-xs font-semibold tracking-wide text-text-secondary tabular-nums" data-id="element-78">
              {currentStep + 1} / {steps.length}
            </span>
          </div>

          <div className="w-16 flex justify-end" data-id="element-79">
            {currentStep < steps.length - 1 ? <button onClick={nextStep} className="w-14 h-14 rounded-full bg-primary flex items-center justify-center text-secondary shadow-[0_0_20px_rgba(147,224,30,0.3)] hover:scale-105 transition-transform" data-id="element-80">
                <ChevronRight size={28} data-id="element-81" />
              </button> : <div className="w-14 h-14" data-id="element-82" />}
          </div>
        </div>

        {/* Behind-the-design strategy panel, anchored to the current screen */}
        <StrategyPanel open={showStrategy} onClose={() => setShowStrategy(false)} activeIndex={currentStep} data-id="element-83" />
      </div>
    </div>;
}
// --- Data knowledge view (behind-the-scenes Snak insights) ---
// A hidden, maker-only strategic dashboard modelling what this self-persuasion
// Snak reveals beyond clicks. Two pages: what was captured, then segmentation
// and follow-up recommendations. Figures are illustrative (n = 121).
const SAMPLE_N = 121;
function DataInsightsScreen({
  onBack
}: {
  onBack: () => void;
}) {
  const [page, setPage] = useState<1 | 2>(1);
  // ---- Page 1 data ----
  const kpis = [{
    icon: Eye,
    value: '121',
    label: 'Views'
  }, {
    icon: Users,
    value: '78',
    label: 'Completed the self-check'
  }, {
    icon: MousePointerClick,
    value: '64',
    label: 'Flipped both patient cards'
  }, {
    icon: ArrowUpRight,
    value: '41',
    label: 'Clicked a next-step CTA'
  }];
  const recognition = [{
    label: 'Often',
    pct: 42,
    highlight: false
  }, {
    label: 'Sometimes',
    pct: 47,
    highlight: true
  }, {
    label: 'Rarely',
    pct: 11,
    highlight: false
  }];
  const importance = [{
    label: 'High importance (8–10)',
    pct: 54,
    highlight: true
  }, {
    label: 'Moderate importance (5–7)',
    pct: 34,
    highlight: false
  }, {
    label: 'Low importance (1–4)',
    pct: 12,
    highlight: false
  }];
  const motivation = [{
    label: 'Recurrence can change the clinical picture',
    pct: 31,
    highlight: true
  }, {
    label: 'Repeated flares increase patient burden',
    pct: 28,
    highlight: false
  }, {
    label: 'Appearance alone can be misleading',
    pct: 24,
    highlight: false
  }, {
    label: 'All of the above',
    pct: 17,
    highlight: false
  }];
  // ---- Page 2 data ----
  const segments = [{
    icon: UserCheck,
    name: 'Brand-ready HCPs',
    size: '22 of 121 views',
    criteria: ['High importance score', 'Opened both patient cards', 'Clicked “Explore ANZUPGO”'],
    insight: 'These HCPs already recognise that chronicity changes the conversation.',
    next: 'Rep follow-up or product-fit Snak: “Where ANZUPGO may fit in moderate-to-severe CHE.”'
  }, {
    icon: GraduationCap,
    name: 'Disease-aware, needs support',
    size: '37 of 121 views',
    criteria: ['Moderate or high importance score', 'Opened both patient cards', 'Clicked “Download CHE self-check” or “See patient cases”'],
    insight: 'Engaged, but may prefer practical tools before product detail.',
    next: 'Send a CHE identification checklist or a case-based trailer Snak.'
  }, {
    icon: Heart,
    name: 'Burden-motivated HCPs',
    size: '28 of 121 views',
    criteria: ['Selected “Repeated flares increase patient burden”'],
    insight: 'Patient burden is the strongest entry point for this group.',
    next: 'Follow up with patient burden, recurrence impact, and quality-of-life content.'
  }, {
    icon: Eye,
    name: 'Awareness / education segment',
    size: '34 of 121 views',
    criteria: ['Low importance score', 'Did not complete reveal', 'No CTA click'],
    insight: 'May not yet see chronicity as a key decision factor.',
    next: 'Retarget with simple disease education: “Two similar hands. Two very different histories.”'
  }];
  const tracks = [{
    name: 'Product-fit track',
    body: 'For high-readiness HCPs who clicked Explore ANZUPGO.'
  }, {
    name: 'Clinical education track',
    body: 'For HCPs who engaged but need more support on CHE definition, recurrence, and duration.'
  }, {
    name: 'Awareness track',
    body: 'For low-readiness HCPs who need a simpler chronicity message first.'
  }];
  // Reusable animated horizontal bar row.
  const BarRow = ({
    label,
    pct,
    highlight,
    delay
  }: {
    label: string;
    pct: number;
    highlight?: boolean;
    delay: number;
  }) => <div data-id="element-84">
      <div className="flex items-center justify-between mb-1.5 gap-3" data-id="element-85">
        <span className="text-[13px] text-text-secondary leading-snug" data-id="element-86">
          {label}
        </span>
        <span className={`text-sm font-bold tabular-nums shrink-0 ${highlight ? 'text-primary' : 'text-text-primary'}`} data-id="element-87">
          {pct}%
        </span>
      </div>
      <div className="h-2.5 bg-surface rounded-full overflow-hidden" data-id="element-88">
        <motion.div initial={{
        width: 0
      }} animate={{
        width: `${pct}%`
      }} transition={{
        duration: 0.7,
        delay,
        ease: 'easeOut'
      }} className={`h-full rounded-full ${highlight ? 'bg-primary' : 'bg-text-secondary/50'}`} data-id="element-89" />
      </div>
    </div>;
  const Section = ({
    kicker,
    title,
    children,
    insight,
    recommendation,
    delay
  }: {
    kicker: string;
    title: string;
    children: React.ReactNode;
    insight: string;
    recommendation: string;
    delay: number;
  }) => <motion.div initial={{
    opacity: 0,
    y: 18
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay,
    duration: 0.4
  }} className="bg-card-bg border border-surface rounded-2xl p-5" data-id="element-90">
      <p className="text-[10px] font-bold uppercase tracking-widest text-primary mb-1" data-id="element-91">
        {kicker}
      </p>
      <h3 className="text-base font-bold text-text-primary leading-snug mb-4" data-id="element-92">
        {title}
      </h3>
      <div className="space-y-3.5 mb-4" data-id="element-93">{children}</div>
      <p className="text-[13px] text-text-secondary leading-relaxed" data-id="element-94">
        {insight}
      </p>
      <div className="flex items-start gap-2 mt-3 pt-3 border-t border-surface" data-id="element-95">
        <Target size={14} className="text-primary shrink-0 mt-0.5" data-id="element-96" />
        <p className="text-[12px] text-text-primary leading-relaxed" data-id="element-97">
          <span className="font-semibold" data-id="element-98">Recommendation: </span>
          {recommendation}
        </p>
      </div>
    </motion.div>;
  return <div className="flex w-full min-h-screen bg-white text-text-primary font-sans overflow-hidden relative selection:bg-primary/30 justify-center sm:py-6" data-id="element-99">
      <div className="w-full max-w-md mx-auto flex flex-col relative z-10 min-h-[100dvh] sm:min-h-0 sm:h-[calc(100dvh-3rem)] bg-secondary anzupgo-waves overflow-hidden sm:rounded-[2rem] shadow-2xl" data-id="element-100">
        {/* Header */}
        <div className="shrink-0 flex items-center justify-between px-6 py-5 border-b border-surface z-20" data-id="element-101">
          <div className="flex items-center gap-2 text-primary" data-id="element-102">
            <BarChart3 size={18} data-id="element-103" />
            <span className="text-xs font-bold uppercase tracking-widest" data-id="element-104">
              Engagement insights
            </span>
          </div>
          <button onClick={onBack} aria-label="Back to the teaser" className="w-9 h-9 -mr-1 rounded-full flex items-center justify-center text-text-secondary hover:text-text-primary hover:bg-card-bg transition-colors" data-id="element-105">
            <X size={18} data-id="element-106" />
          </button>
        </div>

        {/* Page toggle */}
        <div className="shrink-0 flex gap-2 px-6 pt-4" data-id="element-107">
          {[1, 2].map(p => <button key={p} onClick={() => setPage(p as 1 | 2)} className={`flex-1 text-[11px] font-bold uppercase tracking-wider py-2 rounded-full transition-colors ${page === p ? 'bg-primary text-secondary' : 'bg-card-bg text-text-secondary border border-surface'}`} data-id="element-108">
              {p === 1 ? 'What we captured' : 'Segments & actions'}
            </button>)}
        </div>

        <div className="flex-1 overflow-y-auto hide-scrollbar px-6 py-6" data-id="element-109">
          <AnimatePresence mode="wait" data-id="element-110">
            {page === 1 ? <motion.div key="page1" initial={{
            opacity: 0,
            x: 16
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -16
          }} className="space-y-6" data-id="element-111">
                <div data-id="element-112">
                  <h1 className="text-2xl font-bold leading-tight text-text-primary mb-2" data-id="element-113">
                    Beyond engagement: what 121 views can reveal
                  </h1>
                  <p className="text-sm text-text-secondary leading-relaxed" data-id="element-114">
                    This teaser does not only measure clicks. It captures how
                    HCPs recognise the problem, rate its relevance, and choose
                    their next step.
                  </p>
                </div>

                {/* KPI cards */}
                <div className="grid grid-cols-2 gap-3" data-id="element-115">
                  {kpis.map((k, i) => {
                const Icon = k.icon;
                return <motion.div key={k.label} initial={{
                  opacity: 0,
                  scale: 0.94
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} transition={{
                  delay: i * 0.08
                }} className="bg-card-bg border border-surface rounded-2xl p-4" data-id="element-116">
                        <Icon size={16} className="text-primary mb-2" data-id="element-117" />
                        <p className="text-2xl font-bold text-text-primary tabular-nums leading-none" data-id="element-118">
                          {k.value}
                        </p>
                        <p className="text-[11px] text-text-secondary mt-1.5 leading-snug" data-id="element-119">
                          {k.label}
                        </p>
                      </motion.div>;
              })}
                </div>
                <p className="text-[10px] text-text-secondary/50 -mt-2" data-id="element-120">
                  Example data shown for concept purposes.
                </p>

                {/* Section 1 */}
                <Section kicker="Problem recognition" title="Do HCPs recognise hidden chronicity?" insight="89% indicated that visible appearance may sometimes or often fail to show the full hand eczema story." recommendation="This suggests strong relevance for a case-based chronicity message." delay={0.35} data-id="element-121">
                  {recognition.map((r, i) => <BarRow key={r.label} label={r.label} pct={r.pct} highlight={r.highlight} delay={0.4 + i * 0.1} data-id="element-122" />)}
                </Section>

                {/* Section 2 */}
                <Section kicker="Importance rating" title="How important is uncovering recurrence or duration?" insight="More than half rated uncovering chronicity as highly important before deciding the next step." recommendation="High scorers are likely ready for treatment-fit content. Moderate scorers may need more disease education around CHE definition and recurrence." delay={0.5} data-id="element-123">
                  {importance.map((r, i) => <BarRow key={r.label} label={r.label} pct={r.pct} highlight={r.highlight} delay={0.55 + i * 0.1} data-id="element-124" />)}
                </Section>

                {/* Section 3 */}
                <Section kicker="Motivation driver" title="Why did they not choose a lower score?" insight="The strongest driver was clinical relevance, followed closely by patient burden." recommendation="Follow-up content should combine clinical identification with patient burden, rather than only showing product claims." delay={0.65} data-id="element-125">
                  {motivation.map((r, i) => <BarRow key={r.label} label={r.label} pct={r.pct} highlight={r.highlight} delay={0.7 + i * 0.1} data-id="element-126" />)}
                </Section>

                <button onClick={() => setPage(2)} className="w-full bg-primary text-secondary font-bold py-3.5 rounded-full flex items-center justify-center gap-2 text-sm mt-2" data-id="element-127">
                  See segments &amp; follow-up <ArrowRight size={18} data-id="element-128" />
                </button>
              </motion.div> : <motion.div key="page2" initial={{
            opacity: 0,
            x: 16
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -16
          }} className="space-y-6" data-id="element-129">
                <div data-id="element-130">
                  <h1 className="text-2xl font-bold leading-tight text-text-primary mb-2" data-id="element-131">
                    From views to actionable HCP segments
                  </h1>
                  <p className="text-sm text-text-secondary leading-relaxed" data-id="element-132">
                    The same teaser can help identify who is brand-ready, who
                    needs education, and what follow-up message should come
                    next.
                  </p>
                </div>

                {/* Segment cards */}
                <div className="space-y-3" data-id="element-133">
                  {segments.map((s, i) => {
                const Icon = s.icon;
                return <motion.div key={s.name} initial={{
                  opacity: 0,
                  y: 16
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: i * 0.08
                }} className="bg-card-bg border border-surface rounded-2xl p-5" data-id="element-134">
                        <div className="flex items-center gap-3 mb-3" data-id="element-135">
                          <div className="w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center text-primary shrink-0" data-id="element-136">
                            <Icon size={18} data-id="element-137" />
                          </div>
                          <div data-id="element-138">
                            <p className="text-sm font-bold text-text-primary leading-tight" data-id="element-139">
                              {s.name}
                            </p>
                            <p className="text-[11px] text-primary font-semibold mt-0.5" data-id="element-140">
                              {s.size}
                            </p>
                          </div>
                        </div>
                        <ul className="space-y-1 mb-3" data-id="element-141">
                          {s.criteria.map(c => <li key={c} className="flex items-start gap-2 text-[12px] text-text-secondary leading-snug" data-id="element-142">
                              <span className="text-primary mt-0.5" data-id="element-143">•</span>
                              {c}
                            </li>)}
                        </ul>
                        <p className="text-[12px] text-text-secondary leading-relaxed mb-3" data-id="element-144">
                          {s.insight}
                        </p>
                        <div className="flex items-start gap-2 pt-3 border-t border-surface" data-id="element-145">
                          <ArrowUpRight size={14} className="text-primary shrink-0 mt-0.5" data-id="element-146" />
                          <p className="text-[12px] text-text-primary leading-relaxed" data-id="element-147">
                            <span className="font-semibold" data-id="element-148">Next step: </span>
                            {s.next}
                          </p>
                        </div>
                      </motion.div>;
              })}
                </div>

                {/* Final summary card */}
                <div className="bg-primary/10 border border-primary/40 rounded-2xl p-5" data-id="element-149">
                  <div className="flex items-center gap-2 text-primary mb-3" data-id="element-150">
                    <Target size={16} data-id="element-151" />
                    <p className="text-xs font-bold uppercase tracking-widest" data-id="element-152">
                      Recommended campaign action
                    </p>
                  </div>
                  <p className="text-[13px] text-text-secondary mb-4 leading-relaxed" data-id="element-153">
                    Use the teaser data to split follow-up into three tracks:
                  </p>
                  <div className="space-y-3" data-id="element-154">
                    {tracks.map((t, i) => <div key={t.name} className="flex items-start gap-3" data-id="element-155">
                        <div className="shrink-0 w-6 h-6 rounded-full bg-primary text-secondary font-bold flex items-center justify-center text-xs mt-0.5" data-id="element-156">
                          {i + 1}
                        </div>
                        <div data-id="element-157">
                          <p className="text-sm font-bold text-text-primary leading-snug" data-id="element-158">
                            {t.name}
                          </p>
                          <p className="text-[12px] text-text-secondary leading-relaxed mt-0.5" data-id="element-159">
                            {t.body}
                          </p>
                        </div>
                      </div>)}
                  </div>
                  <p className="text-[13px] text-text-primary leading-relaxed mt-4 pt-4 border-t border-primary/30" data-id="element-160">
                    The value is not only knowing that 121 HCPs viewed the
                    teaser. It is knowing{' '}
                    <span className="font-semibold" data-id="element-161">which HCPs are ready</span>{' '}
                    for ANZUPGO content, which need more CHE education, and
                    which message is most likely to move each group forward.
                  </p>
                </div>

                <p className="text-[10px] text-text-secondary/50 leading-relaxed" data-id="element-162">
                  All numbers are illustrative and should be replaced with real
                  Snak analytics after launch.
                </p>

                <button onClick={onBack} className="w-full bg-primary text-secondary font-bold py-3.5 rounded-full flex items-center justify-center gap-2 text-sm" data-id="element-163">
                  Continue the self-check <ArrowRight size={18} data-id="element-164" />
                </button>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </div>;
}
// --- Storyboard (traditional review board with real page images) ---
// Renders a true miniature of every Snak screen (banner + each teaser step,
// including the CTA/replay end state) with an editable comment area per page,
// exportable to PDF (screens rasterised with html2canvas).
const noop = () => {};
const FRAME_W = 300;
// Fallback height used before the real content height is measured.
const FRAME_H = 620;
// A faithful mini phone-frame that renders a real teaser screen inside the
// same dark ANZUPGO shell used by the live Snak. Height grows with the
// content so no text is ever clipped.
function MiniShell({
  children
}: {
  children: React.ReactNode;
}) {
  return <div style={{
    width: FRAME_W,
    minHeight: FRAME_H
  }} className="bg-secondary anzupgo-waves flex flex-col text-text-primary font-sans relative" data-id="element-165">
      <div className="w-full flex justify-between items-center px-6 py-5 shrink-0" data-id="element-166">
        <div className="flex items-center gap-1.5 text-primary" data-id="element-167">
          <ULogo className="w-5 h-5" data-id="element-168" />
          <span className="text-lg font-bold tracking-tight text-text-primary flex items-center gap-1" data-id="element-169">
            Anzupgo<sup className="text-[10px] mt-1" data-id="element-170">®</sup>
          </span>
        </div>
        <span className="text-[8px] font-bold tracking-[0.2em] text-text-secondary" data-id="element-171">
          LEO
        </span>
      </div>
      <div className="flex-1 flex flex-col relative px-6 py-4" data-id="element-172">{children}</div>
    </div>;
}
// Banner has its own strategy note (it sits before the teaser steps);
// every other frame maps to an index in STRATEGY_NOTES.
const BANNER_STRATEGY: StrategyNote = {
  tag: 'The Hook',
  title: 'A clickable challenge, not an ad',
  body: 'Placed in a journal, the banner poses a question the eye cannot answer — “Which patient has Chronic Hand Eczema?”. It hooks the HCP with curiosity and the realisation that chronicity can’t be judged from appearance, drawing them into the Snak on their own terms rather than through a product claim.'
};
// Resolve a frame's strategy note (index -1 = banner).
function STRATEGY_FOR(index: number): StrategyNote {
  return index < 0 ? BANNER_STRATEGY : STRATEGY_NOTES[index];
}
// Each frame renders the real component; banner renders its own full page.
// strategyIndex maps the frame to a STRATEGY_NOTES entry (reply frames reuse
// their parent screen's note); the banner uses BANNER_STRATEGY.
const STORYBOARD_FRAMES: {
  id: string;
  name: string;
  strategyIndex: number;
  render: () => React.ReactNode;
}[] = [{
  id: 'banner',
  name: 'Banner Ad (Hook)',
  strategyIndex: -1,
  render: () => <div style={{
    width: FRAME_W,
    height: FRAME_H
  }} className="overflow-hidden bg-white" data-id="element-173">
        <div style={{
      width: FRAME_W * 2.4,
      transform: 'scale(0.417)',
      transformOrigin: 'top left'
    }} data-id="element-174">
          <BannerAd onLaunch={noop} data-id="element-175" />
        </div>
      </div>
}, {
  id: 'flip',
  name: 'Flip Reveal',
  strategyIndex: 0,
  render: () => <MiniShell data-id="element-176">
        <FlipRevealScreen initiallyRevealed data-id="element-177" />
      </MiniShell>
}, {
  id: 'problem',
  name: 'Problem Mirror',
  strategyIndex: 1,
  render: () => <MiniShell data-id="element-178">
        <ProblemMirrorScreen data-id="element-179" />
      </MiniShell>
}, {
  id: 'problem-reply',
  name: 'Problem Mirror — reply',
  strategyIndex: 1,
  render: () => <MiniShell data-id="element-180">
        <ProblemMirrorScreen initialSelected="C" data-id="element-181" />
      </MiniShell>
}, {
  id: 'rating',
  name: 'Self-Rating',
  strategyIndex: 2,
  render: () => <MiniShell data-id="element-182">
        <SelfRatingScreen data-id="element-183" />
      </MiniShell>
}, {
  id: 'persuasion',
  name: 'Why not lower?',
  strategyIndex: 3,
  render: () => <MiniShell data-id="element-184">
        <SelfPersuasionScreen data-id="element-185" />
      </MiniShell>
}, {
  id: 'persuasion-reply',
  name: 'Why not lower? — reply',
  strategyIndex: 3,
  render: () => <MiniShell data-id="element-186">
        <SelfPersuasionScreen initialSelected="A" data-id="element-187" />
      </MiniShell>
}, {
  id: 'definition',
  name: 'Definition',
  strategyIndex: 4,
  render: () => <MiniShell data-id="element-188">
        <DefinitionScreen data-id="element-189" />
      </MiniShell>
}, {
  id: 'data',
  name: 'Prevalence',
  strategyIndex: 5,
  render: () => <MiniShell data-id="element-190">
        <DataScreen data-id="element-191" />
      </MiniShell>
}, {
  id: 'brand',
  name: 'Brand Bridge',
  strategyIndex: 6,
  render: () => <MiniShell data-id="element-192">
        <BrandBridgeScreen data-id="element-193" />
      </MiniShell>
}, {
  id: 'cta',
  name: 'CTA & Replay',
  strategyIndex: 7,
  render: () => <MiniShell data-id="element-194">
        <CTAScreen onReplay={noop} onExplore={noop} data-id="element-195" />
      </MiniShell>
}];
function StoryboardScreen({
  onBack
}: {
  onBack: () => void;
}) {
  const [comments, setComments] = useState<Record<string, string>>({});
  const [exporting, setExporting] = useState(false);
  // Measured natural height of each frame (content-driven, so nothing clips).
  const [heights, setHeights] = useState<Record<string, number>>({});
  // Refs to each full-size (unscaled) frame node, used for rasterising.
  const frameRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const setComment = (id: string, value: string) => setComments(prev => ({
    ...prev,
    [id]: value
  }));
  // Measure real frame heights (re-run after images/animations settle).
  useEffect(() => {
    const measure = () => {
      const next: Record<string, number> = {};
      STORYBOARD_FRAMES.forEach(f => {
        const el = frameRefs.current[f.id];
        if (el) next[f.id] = el.offsetHeight;
      });
      setHeights(prev => {
        const changed = STORYBOARD_FRAMES.some(f => next[f.id] && next[f.id] !== prev[f.id]);
        return changed ? {
          ...prev,
          ...next
        } : prev;
      });
    };
    measure();
    const t1 = setTimeout(measure, 400);
    const t2 = setTimeout(measure, 1500);
    window.addEventListener('resize', measure);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener('resize', measure);
    };
  }, []);
  const frameH = (id: string) => heights[id] || FRAME_H;
  // Display scale so the 300px-wide frame fits the storyboard card.
  const DISPLAY_W = 252;
  const scale = DISPLAY_W / FRAME_W;
  const downloadPdf = async () => {
    setExporting(true);
    try {
      const doc = new jsPDF({
        unit: 'pt',
        format: 'a4'
      });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 40;
      // Cover heading
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.text('ANZUPGO Snak — Storyboard', margin, margin + 4);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(120);
      doc.text('Chronic Hand Eczema self-persuasion teaser · review & comments', margin, margin + 22);
      doc.setTextColor(0);
      const contentW = pageW - margin * 2;
      // --- Intro: the Instant Influence theory ---
      let iy = margin + 56;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(90, 150, 20);
      doc.text('THE THEORY BEHIND THE STORYBOARD', margin, iy);
      iy += 20;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.setTextColor(0);
      doc.text(INSTANT_INFLUENCE_THEORY.title, margin, iy);
      iy += 16;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(120);
      doc.text(INSTANT_INFLUENCE_THEORY.source, margin, iy);
      iy += 20;
      doc.setFontSize(11);
      doc.setTextColor(40);
      for (const para of INSTANT_INFLUENCE_THEORY.paragraphs) {
        const lines = doc.splitTextToSize(para, contentW);
        if (iy + lines.length * 15 > pageH - margin) {
          doc.addPage();
          iy = margin;
        }
        doc.text(lines, margin, iy);
        iy += lines.length * 15 + 10;
      }
      // Frames start on a fresh page after the intro.
      doc.addPage();
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(0);
      doc.text('Frame by frame', margin, margin + 4);
      const imgW = 150;
      const maxImgH = pageH - margin * 2 - 24;
      let y = margin + 24;
      for (let i = 0; i < STORYBOARD_FRAMES.length; i++) {
        const frame = STORYBOARD_FRAMES[i];
        const node = frameRefs.current[frame.id];
        const natH = node ? node.offsetHeight : FRAME_H;
        const imgH = Math.min(imgW * natH / FRAME_W, maxImgH);
        const blockH = imgH + 20;
        if (y + blockH > pageH - margin) {
          doc.addPage();
          y = margin;
        }
        // Frame image
        if (node) {
          try {
            const canvas = await html2canvas(node, {
              backgroundColor: '#141414',
              scale: 2,
              useCORS: true,
              logging: false
            });
            const data = canvas.toDataURL('image/jpeg', 0.92);
            doc.addImage(data, 'JPEG', margin, y, imgW, imgH);
          } catch {
            doc.setDrawColor(210);
            doc.rect(margin, y, imgW, imgH);
          }
        }
        // Text column beside the image
        const tx = margin + imgW + 18;
        const tw = pageW - margin - tx;
        let ty = y + 12;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(0);
        doc.text(`${i + 1}. ${frame.name}`, tx, ty);
        ty += 18;
        // Behind the design note (banner has its own; others map by index)
        const note = STRATEGY_FOR(frame.strategyIndex);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8);
        doc.setTextColor(90, 150, 20);
        doc.text('BEHIND THE DESIGN', tx, ty);
        ty += 12;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9.5);
        doc.setTextColor(0);
        const noteTitleLines = doc.splitTextToSize(`${note.tag} — ${note.title}`, tw);
        doc.text(noteTitleLines, tx, ty);
        ty += noteTitleLines.length * 12 + 2;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(70);
        const noteBodyLines = doc.splitTextToSize(note.body, tw);
        doc.text(noteBodyLines, tx, ty);
        ty += noteBodyLines.length * 11 + 10;
        // Comments
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8);
        doc.setTextColor(120);
        doc.text('COMMENTS', tx, ty);
        ty += 12;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(0);
        const commentText = comments[frame.id]?.trim() || '—';
        const lines = doc.splitTextToSize(commentText, tw);
        doc.text(lines, tx, ty);
        ty += lines.length * 12;
        // Row advances by whichever column is taller.
        y = Math.max(y + imgH, ty) + 20;
      }
      doc.save('anzupgo-snak-storyboard.pdf');
    } finally {
      setExporting(false);
    }
  };
  return <div className="flex w-full min-h-screen bg-white text-text-primary font-sans overflow-hidden relative selection:bg-primary/30 justify-center sm:py-6" data-id="element-196">
      {/* Offscreen full-size frame renders used for high-res PDF capture */}
      <div aria-hidden="true" style={{
      position: 'fixed',
      left: -100000,
      top: 0,
      pointerEvents: 'none'
    }} data-id="element-197">
        {STORYBOARD_FRAMES.map(frame => <div key={frame.id} ref={el => {
        frameRefs.current[frame.id] = el;
      }} style={{
        width: FRAME_W
      }} data-id="element-198">
            {frame.render()}
          </div>)}
      </div>

      <div className="w-full max-w-md mx-auto flex flex-col relative z-10 min-h-[100dvh] sm:min-h-0 sm:h-[calc(100dvh-3rem)] bg-secondary anzupgo-waves overflow-hidden sm:rounded-[2rem] shadow-2xl" data-id="element-199">
        {/* Header */}
        <div className="shrink-0 flex items-center justify-between px-6 py-5 border-b border-surface z-20" data-id="element-200">
          <div className="flex items-center gap-2 text-primary" data-id="element-201">
            <LayoutGrid size={18} data-id="element-202" />
            <span className="text-xs font-bold uppercase tracking-widest" data-id="element-203">
              Storyboard
            </span>
          </div>
          <button onClick={onBack} aria-label="Back to the teaser" className="w-9 h-9 -mr-1 rounded-full flex items-center justify-center text-text-secondary hover:text-text-primary hover:bg-card-bg transition-colors" data-id="element-204">
            <X size={18} data-id="element-205" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto hide-scrollbar px-6 py-6 pb-28" data-id="element-206">
          <h1 className="text-2xl font-bold leading-tight text-text-primary mb-2" data-id="element-207">
            Snak storyboard
          </h1>
          <p className="text-sm text-text-secondary leading-relaxed mb-6" data-id="element-208">
            A real image of every screen, frame by frame. Add review notes per
            page, then export the whole board as a PDF.
          </p>

          {/* Methodology intro — the theory the whole storyboard is built on */}
          <div className="bg-card-bg border border-primary/30 rounded-2xl p-5 mb-6" data-id="element-209">
            <div className="flex items-center gap-2 text-primary mb-2" data-id="element-210">
              <Info size={16} data-id="element-211" />
              <span className="text-[10px] font-bold uppercase tracking-widest" data-id="element-212">
                The theory behind the storyboard
              </span>
            </div>
            <h2 className="text-lg font-bold text-text-primary leading-tight" data-id="element-213">
              {INSTANT_INFLUENCE_THEORY.title}
            </h2>
            <p className="text-[11px] font-semibold text-text-secondary mb-3" data-id="element-214">
              {INSTANT_INFLUENCE_THEORY.source}
            </p>
            <div className="space-y-2.5" data-id="element-215">
              {INSTANT_INFLUENCE_THEORY.paragraphs.map((p, i) => <p key={i} className="text-[13px] text-text-secondary leading-relaxed" data-id="element-216">
                  {p}
                </p>)}
            </div>
          </div>

          <div className="space-y-5" data-id="element-217">
            {STORYBOARD_FRAMES.map((frame, i) => <div key={frame.id} className="bg-card-bg border border-surface rounded-2xl overflow-hidden" data-id="element-218">
                <div className="flex items-center justify-between px-4 pt-4" data-id="element-219">
                  <p className="text-sm font-bold text-text-primary" data-id="element-220">
                    {i + 1}. {frame.name}
                  </p>
                  <span className="text-[9px] font-bold uppercase tracking-wider text-text-secondary" data-id="element-221">
                    Frame {i + 1}
                  </span>
                </div>
                {/* Real, scaled render of the actual page (full height) */}
                <div className="px-4 pt-3 flex justify-center" data-id="element-222">
                  <div className="rounded-xl overflow-hidden border border-surface shadow-lg bg-secondary" style={{
                width: DISPLAY_W,
                height: frameH(frame.id) * scale
              }} data-id="element-223">
                    <div style={{
                  width: FRAME_W,
                  transform: `scale(${scale})`,
                  transformOrigin: 'top left'
                }} data-id="element-224">
                      {frame.render()}
                    </div>
                  </div>
                </div>
                {/* Comment area */}
                <div className="px-4 py-4" data-id="element-225">
                  <label htmlFor={`cmt-${frame.id}`} className="text-[10px] font-bold uppercase tracking-widest text-primary" data-id="element-226">
                    Comments
                  </label>
                  <textarea id={`cmt-${frame.id}`} value={comments[frame.id] || ''} onChange={e => setComment(frame.id, e.target.value)} rows={2} placeholder="Add a review note for this screen…" className="mt-1.5 w-full bg-secondary border border-surface rounded-xl p-3 text-[13px] text-text-primary placeholder:text-text-secondary/40 resize-y focus:outline-none focus:border-primary/60 focus:ring-1 focus:ring-primary/40" data-id="element-227" />
                </div>
              </div>)}
          </div>

          <p className="text-[10px] text-text-secondary/50 leading-relaxed mt-6" data-id="element-228">
            Page images and notes are exported into the PDF. Notes are not saved
            after you close this view.
          </p>
        </div>

        {/* Sticky footer actions */}
        <div className="absolute bottom-0 left-0 right-0 p-6 z-20 bg-gradient-to-t from-secondary via-secondary to-transparent pt-12 flex gap-3" data-id="element-229">
          <button onClick={onBack} className="shrink-0 px-5 rounded-full border border-text-secondary/40 text-text-secondary font-semibold text-sm hover:text-text-primary hover:border-text-secondary transition-colors" data-id="element-230">
            Back
          </button>
          <button onClick={downloadPdf} disabled={exporting} className="flex-1 bg-primary text-secondary font-bold py-3.5 rounded-full shadow-[0_0_20px_rgba(147,224,30,0.3)] flex items-center justify-center gap-2 text-sm disabled:opacity-60" data-id="element-231">
            <Download size={18} data-id="element-232" />
            {exporting ? 'Preparing PDF…' : 'Download storyboard PDF'}
          </button>
        </div>
      </div>
    </div>;
}
// --- Embedded site view (anzupgo.com inside the Snak frame) ---
// Keeps the HCP inside the teaser: the site loads in the same phone frame
// with a lightweight browser chrome, so there is no jump-away drop-off.
function SiteEmbedScreen({
  onBack
}: {
  onBack: () => void;
}) {
  const SITE_URL = 'https://www.anzupgo.com';
  return <div className="flex w-full min-h-screen bg-white text-text-primary font-sans overflow-hidden relative selection:bg-primary/30 justify-center sm:py-6" data-id="element-233">
      <div className="w-full max-w-md mx-auto flex flex-col relative z-10 min-h-[100dvh] sm:min-h-0 sm:h-[calc(100dvh-3rem)] bg-secondary overflow-hidden sm:rounded-[2rem] shadow-2xl" data-id="element-234">
        {/* Mini browser chrome */}
        <div className="shrink-0 flex items-center gap-3 px-4 py-3 bg-secondary border-b border-surface z-20" data-id="element-235">
          <button onClick={onBack} aria-label="Back to the teaser" className="w-9 h-9 -ml-1 rounded-full border border-text-secondary/40 flex items-center justify-center text-text-secondary hover:text-text-primary hover:border-text-secondary transition-all shrink-0" data-id="element-236">
            <ChevronLeft size={20} data-id="element-237" />
          </button>
          <div className="flex-1 flex items-center gap-2 bg-card-bg border border-surface rounded-full px-3 py-2 min-w-0" data-id="element-238">
            <Lock size={12} className="text-primary shrink-0" data-id="element-239" />
            <span className="text-xs text-text-secondary truncate" data-id="element-240">
              www.anzupgo.com
            </span>
          </div>
          <a href={SITE_URL} target="_blank" rel="noopener noreferrer" aria-label="Open anzupgo.com in a new tab" className="w-9 h-9 rounded-full flex items-center justify-center text-text-secondary hover:text-primary transition-colors shrink-0" data-id="element-241">
            <ExternalLink size={16} data-id="element-242" />
          </a>
        </div>

        {/* Embedded site */}
        <div className="flex-1 relative bg-white" data-id="element-243">
          {/* Fallback shown underneath in case the site blocks embedding */}
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8 gap-3 bg-secondary anzupgo-waves" data-id="element-244">
            <Globe size={32} className="text-primary" data-id="element-245" />
            <p className="text-sm text-text-secondary leading-relaxed" data-id="element-246">
              If anzupgo.com doesn’t appear here, it may not allow embedding.
            </p>
            <a href={SITE_URL} target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-primary underline underline-offset-2" data-id="element-247">
              Open www.anzupgo.com
            </a>
          </div>
          <iframe src={SITE_URL} title="ANZUPGO — www.anzupgo.com" className="absolute inset-0 w-full h-full border-0 bg-white" loading="lazy" referrerPolicy="no-referrer-when-downgrade" data-id="element-248" />
        </div>
      </div>
    </div>;
}
// --- Banner Ad Hook (standalone, clickable) ---
// Rendered as if placed inside a printed medical journal / magazine spread.
function BannerAd({
  onLaunch
}: {
  onLaunch: () => void;
}) {
  const handCard = (src: string, label: string, delay: number) => <motion.button onClick={onLaunch} initial={{
    opacity: 0,
    y: 16
  }} animate={{
    opacity: 1,
    y: [0, -6, 0]
  }} transition={{
    opacity: {
      delay,
      duration: 0.5
    },
    y: {
      delay: delay + 0.4,
      duration: 3.2,
      repeat: Infinity,
      ease: 'easeInOut'
    }
  }} whileHover={{
    scale: 1.03
  }} whileTap={{
    scale: 0.97
  }} className="group relative flex-1 rounded-t-[70px] rounded-b-2xl overflow-hidden aspect-[9/16] border border-white/10 shadow-lg focus:outline-none focus:ring-2 focus:ring-primary" data-id="element-249">
      <img src={src} alt={label} className="w-full h-full object-cover" data-id="element-250" />
      {/* Constant subtle vignette + hover green wash */}
      <div className="absolute inset-0 bg-gradient-to-t from-secondary/80 via-transparent to-transparent" data-id="element-251" />
      <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/15 transition-colors duration-300" data-id="element-252" />

      {/* Pulsing "choose" ring */}
      <motion.div animate={{
      opacity: [0.35, 0.9, 0.35],
      scale: [1, 1.08, 1]
    }} transition={{
      duration: 2,
      repeat: Infinity,
      ease: 'easeInOut'
    }} className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5 bg-primary text-secondary text-[11px] font-bold px-3 py-1.5 rounded-full shadow-[0_0_18px_rgba(147,224,30,0.5)]" data-id="element-253">
        <MousePointerClick size={13} data-id="element-254" /> {label}
      </motion.div>
    </motion.button>;
  return <div className="min-h-screen w-full bg-[#eceef0] flex items-center justify-center p-4 sm:p-10 font-sans" data-id="element-255">
      {/* Journal / magazine page */}
      <div className="w-full max-w-5xl bg-white shadow-2xl rounded-sm overflow-hidden border border-black/5" data-id="element-256">
        {/* Masthead */}
        <div className="border-b-2 border-gray-900 px-6 sm:px-12 pt-8 pb-4 flex items-end justify-between" data-id="element-257">
          <div data-id="element-258">
            <h1 className="font-serif text-3xl sm:text-4xl font-black tracking-tight text-gray-900" data-id="element-259">
              The Dermatology Review
            </h1>
            <p className="text-[11px] uppercase tracking-[0.25em] text-gray-500 mt-1" data-id="element-260">
              Clinical Practice · Volume 42 · July 2026
            </p>
          </div>
          <span className="hidden sm:block text-xs text-gray-400 font-medium" data-id="element-261">
            hcp.dermreview.com
          </span>
        </div>

        <div className="px-6 sm:px-12 py-8 sm:py-10" data-id="element-262">
          {/* Article header */}
          <p className="text-xs uppercase tracking-widest text-gray-400 font-bold mb-2" data-id="element-263">
            Review Article
          </p>
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-gray-900 leading-snug mb-6 max-w-3xl" data-id="element-264">
            Recognising the burden of recurrent hand eczema in everyday clinical
            practice
          </h2>

          {/* Two-column article body with the ad embedded */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-265">
            {/* Filler article columns */}
            <div className="hidden md:block space-y-3 text-[11px] leading-relaxed text-gray-400 columns-1" data-id="element-266">
              {Array.from({
              length: 5
            }).map((_, i) => <div key={i} className="space-y-1.5" data-id="element-267">
                  <div className="h-2 bg-gray-200/80 rounded w-full" data-id="element-268" />
                  <div className="h-2 bg-gray-200/80 rounded w-11/12" data-id="element-269" />
                  <div className="h-2 bg-gray-200/80 rounded w-full" data-id="element-270" />
                  <div className="h-2 bg-gray-200/80 rounded w-4/5" data-id="element-271" />
                  <div className="h-2 bg-gray-200/80 rounded w-full" data-id="element-272" />
                </div>)}
            </div>

            {/* The clickable banner ad slot */}
            <div className="md:col-span-2" data-id="element-273">
              <p className="text-[10px] uppercase tracking-[0.3em] text-gray-400 font-bold mb-2 text-center" data-id="element-274">
                Advertisement
              </p>
              <div className="relative bg-secondary anzupgo-waves rounded-2xl overflow-hidden shadow-xl border border-black/10" data-id="element-275">
                {/* Top green accent bar */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-primary z-10" data-id="element-276" />

                {/* Ad header */}
                <div className="flex items-center justify-between px-5 pt-5" data-id="element-277">
                  <div className="flex items-center gap-1.5 text-primary" data-id="element-278">
                    <ULogo className="w-5 h-5" data-id="element-279" />
                    <span className="text-base font-bold tracking-tight text-white" data-id="element-280">
                      Anzupgo<sup className="text-[9px]" data-id="element-281">®</sup>
                    </span>
                  </div>
                  <span className="text-[8px] font-bold tracking-[0.2em] text-white/50" data-id="element-282">
                    LEO
                  </span>
                </div>

                <div className="px-5 pt-5 pb-6" data-id="element-283">
                  <h3 className="text-2xl font-bold text-white text-center leading-tight mb-1" data-id="element-284">
                    Which patient has Chronic Hand Eczema?
                  </h3>
                  <p className="text-sm text-white/60 text-center mb-2" data-id="element-285">
                    They may look similar today — but only the history reveals
                    chronicity.
                  </p>
                  <p className="text-[11px] uppercase tracking-widest text-primary font-bold text-center mb-5" data-id="element-286">
                    Choose Patient A or Patient B
                  </p>

                  {/* Two animated, clickable hand choices */}
                  <div className="flex items-stretch gap-3 relative" data-id="element-287">
                    {handCard(HAND_IMAGE_A, 'Patient A', 0.1)}

                    {/* OR divider */}
                    <div className="flex flex-col items-center justify-center shrink-0" data-id="element-288">
                      <motion.div animate={{
                      scale: [1, 1.12, 1]
                    }} transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: 'easeInOut'
                    }} className="w-9 h-9 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-[10px] font-bold text-white uppercase tracking-wider" data-id="element-289">
                        or
                      </motion.div>
                    </div>

                    {handCard(HAND_IMAGE_B, 'Patient B', 0.25)}
                  </div>

                  <button onClick={onLaunch} className="mt-5 w-full bg-primary text-secondary font-bold py-3 rounded-full shadow-[0_0_20px_rgba(147,224,30,0.3)] hover:scale-[1.02] transition-transform flex items-center justify-center gap-2 text-sm" data-id="element-290">
                    Reveal the answer <ArrowRight size={16} data-id="element-291" />
                  </button>

                  <p className="text-[9px] text-white/40 text-center mt-4 leading-relaxed" data-id="element-292">
                    For healthcare professionals only. MAT-96943 · July 2026
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
function ProblemMirrorScreen({
  initialSelected = null
}: {
  initialSelected?: string | null;
}) {
  const [selected, setSelected] = useState<string | null>(initialSelected);
  const options = [{
    id: 'A',
    label: 'Rarely'
  }, {
    id: 'B',
    label: 'Sometimes'
  }, {
    id: 'C',
    label: 'Often'
  }];
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="flex flex-col w-full h-full justify-center" data-id="element-293">
      <div className="flex items-center gap-2 text-primary font-bold text-xs tracking-widest uppercase mb-4" data-id="element-294">
        <div className="w-6 h-px bg-primary" data-id="element-295" />
        PROBLEM MIRROR
      </div>
      <div className="w-full h-36 rounded-2xl overflow-hidden mb-6 border border-surface shadow-lg relative" data-id="element-296">
        <img src={LIFE_SCRATCH} alt="A person scratching an eczema flare on the back of the hand" className="w-full h-full object-cover" data-id="element-297" />
        <div className="absolute inset-0 bg-gradient-to-t from-secondary/70 to-transparent" data-id="element-298" />
      </div>
      <h1 className="text-3xl font-bold mb-6 leading-tight text-text-primary" data-id="element-299">
        How often does today's appearance fail to show the full burden?
      </h1>
      <p className="text-base text-text-secondary mb-8" data-id="element-300">
        In your practice, how often do you see hand eczema patients where the
        visible flare may not tell the full story?
      </p>

      <div className="space-y-3 mb-8" data-id="element-301">
        {options.map((opt, i) => <motion.button key={opt.id} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: i * 0.1
      }} onClick={() => setSelected(opt.id)} className={`w-full text-left p-5 rounded-2xl border transition-all ${selected === opt.id ? 'border-primary bg-primary/10 text-primary font-semibold' : 'border-surface bg-card-bg text-text-primary hover:border-text-secondary/30'}`} data-id="element-302">
            <span className="mr-3 text-sm opacity-70" data-id="element-303">{opt.id}.</span>{' '}
            {opt.label}
          </motion.button>)}
      </div>

      <AnimatePresence data-id="element-304">
        {selected && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-surface p-5 rounded-2xl border border-text-secondary/10 mb-8" data-id="element-305">
            <p className="text-sm text-text-secondary italic" data-id="element-306">
              "Many HCPs recognise that appearance alone can be misleading —
              especially when flares recur over time."
            </p>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}
function SelfRatingScreen() {
  const [value, setValue] = useState(5);
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="flex flex-col w-full h-full justify-center" data-id="element-307">
      <div className="flex items-center gap-2 text-primary font-bold text-xs tracking-widest uppercase mb-4" data-id="element-308">
        <div className="w-6 h-px bg-primary" data-id="element-309" />
        SELF-RATING
      </div>
      <h1 className="text-3xl font-bold mb-6 leading-tight text-text-primary" data-id="element-310">
        How important is uncovering chronicity?
      </h1>
      <p className="text-base text-text-secondary mb-12" data-id="element-311">
        On a scale from 1–10, how important is it to uncover recurrence or
        duration before deciding the next step?
      </p>

      <div className="flex flex-col items-center mb-16 bg-surface p-8 rounded-3xl border border-white/10" data-id="element-312">
        <motion.div key={value} initial={{
        scale: 0.8,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} className="w-24 h-24 rounded-full bg-primary/10 border-2 border-primary flex items-center justify-center mb-10 shadow-[0_0_30px_rgba(147,224,30,0.15)]" data-id="element-313">
          <span className="text-4xl font-bold text-primary" data-id="element-314">{value}</span>
        </motion.div>

        <input type="range" min="1" max="10" value={value} onChange={e => setValue(parseInt(e.target.value))} className="w-full h-2 bg-white/20 rounded-lg appearance-none cursor-pointer accent-primary" data-id="element-315" />

        <div className="flex justify-between w-full mt-6 text-xs text-text-secondary font-medium uppercase tracking-wider" data-id="element-316">
          <span data-id="element-317">Not important</span>
          <span data-id="element-318">Essential</span>
        </div>
      </div>
    </motion.div>;
}
function SelfPersuasionScreen({
  initialSelected = null
}: {
  initialSelected?: string | null;
}) {
  const [selected, setSelected] = useState<string | null>(initialSelected);
  const options = [{
    id: 'A',
    label: 'Recurrence can change the clinical picture'
  }, {
    id: 'B',
    label: 'Repeated flares can increase patient burden'
  }, {
    id: 'C',
    label: 'Appearance alone can be misleading'
  }, {
    id: 'D',
    label: 'All of the above'
  }];
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="flex flex-col w-full h-full justify-center" data-id="element-319">
      <div className="flex items-center gap-2 text-primary font-bold text-xs tracking-widest uppercase mb-4" data-id="element-320">
        <div className="w-6 h-px bg-primary" data-id="element-321" />
        REFLECTION
      </div>
      <h1 className="text-3xl font-bold mb-4 leading-tight text-text-primary" data-id="element-322">
        Why not lower?
      </h1>
      <p className="text-base text-text-secondary mb-8" data-id="element-323">
        What makes chronicity worth asking about?
      </p>

      <div className="space-y-3 mb-8" data-id="element-324">
        {options.map((opt, i) => <motion.button key={opt.id} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: i * 0.1
      }} onClick={() => setSelected(opt.id)} className={`w-full text-left p-5 rounded-2xl border transition-all ${selected === opt.id ? 'border-primary bg-primary/10 text-primary font-semibold shadow-[0_0_15px_rgba(147,224,30,0.15)]' : 'border-surface bg-card-bg text-text-primary hover:border-text-secondary/30'}`} data-id="element-325">
            <span className="mr-3 text-sm opacity-70" data-id="element-326">{opt.id}.</span>{' '}
            {opt.label}
          </motion.button>)}
      </div>

      <AnimatePresence data-id="element-327">
        {selected && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-primary/10 p-5 rounded-2xl border border-primary/20 mb-8" data-id="element-328">
            <p className="text-sm text-primary font-medium" data-id="element-329">
              Exactly. Chronicity is not always visible in the hands today. It
              often lives in the patient history.
            </p>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}
// Patient profile portrait (head & shoulders) shown on the flip-card backs.
function PatientPortrait({
  variant
}: {
  variant: 'A' | 'B';
}) {
  const src = variant === 'B' ? PATIENT_B_PORTRAIT : PATIENT_A_PORTRAIT;
  return <img src={src} alt={`Portrait of patient ${variant}`} className="w-full h-full object-cover object-top" data-id="element-330" />;
}
function FlipRevealScreen({
  initiallyRevealed = false
}: {
  initiallyRevealed?: boolean;
}) {
  const [flippedA, setFlippedA] = useState(initiallyRevealed);
  const [flippedB, setFlippedB] = useState(initiallyRevealed);
  const bothFlipped = flippedA && flippedB;
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="flex flex-col items-center w-full justify-center h-full" data-id="element-331">
      <div className="flex items-center gap-2 text-primary font-bold text-xs tracking-widest uppercase mb-4 w-full" data-id="element-332">
        <div className="w-6 h-px bg-primary" data-id="element-333" />
        REVEAL
      </div>
      <h1 className="text-3xl font-bold mb-8 text-left w-full leading-tight text-text-primary" data-id="element-334">
        They look similar today.
      </h1>

      <div className="flex gap-4 w-full mb-10" data-id="element-335">
        {/* Card A */}
        <div className="relative flex-1 aspect-[9/16] cursor-pointer perspective-1000" onClick={() => setFlippedA(true)} data-id="element-336">
          <motion.div className="w-full h-full transition-all duration-500 preserve-3d relative" animate={{
          rotateY: flippedA ? 180 : 0
        }} data-id="element-337">
            {/* Front */}
            <div className="absolute inset-0 backface-hidden rounded-t-[80px] rounded-b-2xl overflow-hidden shadow-lg border border-surface" data-id="element-338">
              <img src={HAND_IMAGE_A} alt="Patient A" className="w-full h-full object-cover" data-id="element-339" />
              <div className="absolute inset-0 bg-secondary/40 flex items-center justify-center" data-id="element-340">
                {!flippedA && <span className="bg-surface/90 text-text-primary text-xs font-bold px-4 py-2 rounded-full shadow-sm backdrop-blur-sm border border-text-secondary/20" data-id="element-341">
                    Tap to reveal
                  </span>}
              </div>
            </div>
            {/* Back — Patient A profile */}
            <div className="absolute inset-0 backface-hidden rounded-t-[80px] rounded-b-2xl bg-white border border-surface overflow-hidden flex flex-col rotate-y-180" data-id="element-342">
              <div className="relative flex-1 overflow-hidden" data-id="element-343">
                <PatientPortrait variant="A" data-id="element-344" />
                <span className="absolute top-4 left-1/2 -translate-x-1/2 bg-primary text-secondary text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full shadow" data-id="element-345">
                  Patient A
                </span>
              </div>
              <div className="p-3 text-center bg-white border-t-2 border-primary/30" data-id="element-346">
                <p className="text-sm font-bold text-gray-900 leading-tight" data-id="element-347">
                  First flare
                </p>
                <p className="text-[11px] text-gray-500 leading-snug mt-0.5" data-id="element-348">
                  Symptoms started recently.
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Card B */}
        <div className="relative flex-1 aspect-[9/16] cursor-pointer perspective-1000" onClick={() => setFlippedB(true)} data-id="element-349">
          <motion.div className="w-full h-full transition-all duration-500 preserve-3d relative" animate={{
          rotateY: flippedB ? 180 : 0
        }} data-id="element-350">
            {/* Front */}
            <div className="absolute inset-0 backface-hidden rounded-t-[80px] rounded-b-2xl overflow-hidden shadow-lg border border-surface" data-id="element-351">
              <img src={HAND_IMAGE_B} alt="Patient B" className="w-full h-full object-cover" data-id="element-352" />
              <div className="absolute inset-0 bg-secondary/40 flex items-center justify-center" data-id="element-353">
                {!flippedB && <span className="bg-surface/90 text-text-primary text-xs font-bold px-4 py-2 rounded-full shadow-sm backdrop-blur-sm border border-text-secondary/20" data-id="element-354">
                    Tap to reveal
                  </span>}
              </div>
            </div>
            {/* Back — Patient B profile */}
            <div className="absolute inset-0 backface-hidden rounded-t-[80px] rounded-b-2xl bg-white border-2 border-primary overflow-hidden flex flex-col rotate-y-180 shadow-[0_0_20px_rgba(147,224,30,0.15)]" data-id="element-355">
              <div className="relative flex-1 overflow-hidden" data-id="element-356">
                <PatientPortrait variant="B" data-id="element-357" />
                <span className="absolute top-4 left-1/2 -translate-x-1/2 bg-primary text-secondary text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full shadow" data-id="element-358">
                  Patient B
                </span>
              </div>
              <div className="p-3 text-center bg-white border-t-2 border-primary" data-id="element-359">
                <p className="text-sm font-bold text-gray-900 leading-tight" data-id="element-360">
                  Recurring flares for 8 months
                </p>
                <p className="text-[11px] text-gray-500 leading-snug mt-0.5" data-id="element-361">
                  The visible flare is only part of the story.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <AnimatePresence data-id="element-362">
        {bothFlipped && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="flex flex-col items-center w-full text-center" data-id="element-363">
            <p className="text-xl font-bold text-text-primary leading-snug" data-id="element-364">
              They may look similar today.
            </p>
            <p className="text-xl font-bold text-primary leading-snug" data-id="element-365">
              But their histories are very different.
            </p>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}
function DefinitionScreen() {
  const [showLine, setShowLine] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setShowLine(true), 1500);
    return () => clearTimeout(timer);
  }, []);
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="flex flex-col w-full h-full justify-center" data-id="element-366">
      <div className="flex items-center gap-2 text-primary font-bold text-xs tracking-widest uppercase mb-4" data-id="element-367">
        <div className="w-6 h-px bg-primary" data-id="element-368" />
        ABOUT YOUR CONDITION
      </div>
      <div className="w-full h-32 rounded-2xl overflow-hidden mb-5 border border-surface shadow-lg relative" data-id="element-369">
        <img src={LIFE_PIANO} alt="Hands at a piano — everyday activities affected by hand eczema" className="w-full h-full object-cover" data-id="element-370" />
        <div className="absolute inset-0 bg-gradient-to-t from-secondary/70 to-transparent" data-id="element-371" />
      </div>
      <h1 className="text-3xl font-bold mb-4 leading-tight text-text-primary" data-id="element-372">
        CHE is defined by duration or recurrence — not appearance alone.
      </h1>
      <p className="text-base text-text-secondary mb-8" data-id="element-373">
        When chronicity is not uncovered, patients may remain in a cycle of
        repeated flare management while the burden continues.
      </p>

      <div className="relative w-full h-40 mb-12 flex flex-col justify-center bg-card-bg rounded-3xl border border-surface p-6" data-id="element-374">
        {/* Timeline base */}
        <div className="absolute left-6 right-6 h-1 bg-surface top-1/2 -translate-y-1/2 rounded-full" data-id="element-375" />

        {/* Flares */}
        <div className="absolute left-6 right-6 top-1/2 -translate-y-1/2 flex justify-between" data-id="element-376">
          {[0, 1, 2].map(i => <motion.div key={i} initial={{
          scale: 0,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          delay: i * 0.5
        }} className="w-4 h-4 rounded-full bg-red-500 relative z-10 shadow-[0_0_15px_rgba(239,68,68,0.4)]" data-id="element-377">
              <div className="absolute -top-8 left-1/2 -translate-x-1/2 text-[10px] font-bold text-text-secondary uppercase tracking-wider" data-id="element-378">
                Flare
              </div>
            </motion.div>)}
        </div>

        {/* Chronic framing */}
        <motion.div initial={{
        width: 0,
        opacity: 0
      }} animate={{
        width: showLine ? 'calc(100% - 3rem)' : 0,
        opacity: showLine ? 1 : 0
      }} transition={{
        duration: 0.8,
        ease: 'easeInOut'
      }} className="absolute left-6 h-12 border-2 border-primary rounded-xl top-1/2 -translate-y-1/2 z-0 bg-primary/10" data-id="element-379" />

        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: showLine ? 1 : 0,
        y: showLine ? 0 : 10
      }} transition={{
        delay: 0.8
      }} className="absolute -bottom-4 left-0 right-0 text-center text-sm font-bold text-primary" data-id="element-380">
          Recurring over months = chronicity signal
        </motion.div>
      </div>
    </motion.div>;
}
function DataScreen() {
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="flex flex-col w-full h-full justify-center" data-id="element-381">
      <div className="flex items-center gap-2 text-primary font-bold text-xs tracking-widest uppercase mb-4" data-id="element-382">
        <div className="w-6 h-px bg-primary" data-id="element-383" />
        PREVALENCE
      </div>
      <div className="w-full h-32 rounded-2xl overflow-hidden mb-5 border border-surface shadow-lg relative" data-id="element-384">
        <img src={DATA_HANDS} alt="Close-up of a person examining their hand affected by chronic hand eczema" className="w-full h-full object-cover" data-id="element-385" />
        <div className="absolute inset-0 bg-gradient-to-t from-secondary/70 to-transparent" data-id="element-386" />
      </div>
      <h1 className="text-3xl font-bold mb-6 leading-tight text-text-primary" data-id="element-387">
        Chronic hand eczema may be more common than it looks.
      </h1>
      <p className="text-base text-text-secondary mb-10" data-id="element-388">
        As many as 1 in 10 adults experience chronic hand eczema (CHE).
      </p>

      <div className="bg-card-bg rounded-3xl p-8 mb-6 border border-surface" data-id="element-389">
        <h3 className="text-xs font-bold text-text-secondary uppercase tracking-widest mb-6 text-center" data-id="element-390">
          General Adult Population
        </h3>
        <div className="grid grid-cols-5 gap-4 mb-6" data-id="element-391">
          {Array.from({
          length: 10
        }).map((_, i) => <motion.div key={i} initial={{
          scale: 0,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          delay: i * 0.05
        }} className="flex justify-center" data-id="element-392">
              <User size={28} className={i === 9 ? 'text-primary fill-primary drop-shadow-[0_0_8px_rgba(147,224,30,0.5)]' : 'text-text-secondary/40 fill-text-secondary/20'} data-id="element-393" />
            </motion.div>)}
        </div>
        <p className="text-center font-bold text-text-primary text-lg" data-id="element-394">
          About 1 in 10 adults
        </p>
      </div>

      <p className="text-[10px] leading-relaxed text-text-secondary/60 mt-auto" data-id="element-395">
        References: CHE prevalence data. Figures shown for illustrative
        purposes. Full references available on request.
      </p>
    </motion.div>;
}
function BrandBridgeScreen() {
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="flex flex-col w-full h-full justify-center" data-id="element-396">
      <div className="flex items-center gap-2 text-primary font-bold text-xs tracking-widest uppercase mb-4" data-id="element-397">
        <div className="w-6 h-px bg-primary" data-id="element-398" />
        NEXT STEPS
      </div>
      <div className="w-full h-32 rounded-2xl overflow-hidden mb-5 border border-surface shadow-lg relative" data-id="element-399">
        <img src={LIFE_HOLDING} alt="A person gently holding their hands affected by chronic hand eczema" className="w-full h-full object-cover" data-id="element-400" />
        <div className="absolute inset-0 bg-gradient-to-t from-secondary/70 to-transparent" data-id="element-401" />
      </div>
      <h1 className="text-3xl font-bold mb-4 leading-tight text-text-primary" data-id="element-402">
        When chronicity is uncovered, the conversation can change.
      </h1>
      <p className="text-base text-text-secondary mb-8" data-id="element-403">
        For adults with moderate-to-severe chronic hand eczema for whom topical
        corticosteroids are inadequate or inappropriate, ANZUPGO offers a
        targeted topical treatment option.
      </p>

      <div className="relative pl-8 border-l-2 border-surface space-y-10 mb-12 ml-4" data-id="element-404">
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.2
      }} className="relative" data-id="element-405">
          <div className="absolute -left-[41px] top-0.5 w-5 h-5 rounded-full bg-surface border-4 border-secondary" data-id="element-406" />
          <h3 className="font-bold text-text-primary text-lg" data-id="element-407">
            Recognise chronicity
          </h3>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.6
      }} className="relative" data-id="element-408">
          <div className="absolute -left-[41px] top-0.5 w-5 h-5 rounded-full bg-text-secondary/50 border-4 border-secondary" data-id="element-409" />
          <h3 className="font-bold text-text-primary text-lg" data-id="element-410">
            Assess severity and history
          </h3>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 1.0
      }} className="relative" data-id="element-411">
          <div className="absolute -left-[41px] top-0.5 w-5 h-5 rounded-full bg-primary border-4 border-secondary shadow-[0_0_15px_rgba(147,224,30,0.4)]" data-id="element-412" />
          <h3 className="font-bold text-primary text-lg" data-id="element-413">
            Consider appropriate treatment options
          </h3>
        </motion.div>
      </div>
    </motion.div>;
}
function CTAScreen({
  onReplay,
  onExplore
}: {
  onReplay: () => void;
  onExplore: () => void;
}) {
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="flex flex-col w-full h-full justify-center text-center" data-id="element-414">
      <div className="flex justify-center mb-8" data-id="element-415">
        <ULogo className="w-16 h-16 text-primary" data-id="element-416" />
      </div>

      <h1 className="text-3xl font-bold mb-6 leading-tight text-text-primary" data-id="element-417">
        Could chronicity be hiding in plain sight?
      </h1>
      <p className="text-base text-text-secondary mb-12" data-id="element-418">
        Explore where ANZUPGO may fit in chronic hand eczema.
      </p>

      <div className="space-y-4 w-full mb-12" data-id="element-419">
        <motion.button onClick={onExplore} whileHover={{
        scale: 1.02
      }} whileTap={{
        scale: 0.98
      }} className="w-full bg-primary text-secondary font-bold py-4 px-6 rounded-full shadow-[0_0_20px_rgba(147,224,30,0.3)] flex items-center justify-center gap-2 text-lg" data-id="element-420">
          Explore ANZUPGO <ArrowRight size={20} data-id="element-421" />
        </motion.button>

        <div className="flex justify-center" data-id="element-422">
          <motion.button onClick={onReplay} whileHover={{
          scale: 1.03
        }} whileTap={{
          scale: 0.97
        }} className="text-text-secondary hover:text-text-primary font-semibold py-2 px-5 rounded-full flex items-center justify-center gap-2 text-sm border border-text-secondary/40 hover:border-text-secondary transition-colors" data-id="element-423">
            <RotateCcw size={16} data-id="element-424" /> Watch again
          </motion.button>
        </div>
      </div>

      <div className="bg-card-bg p-5 rounded-2xl text-sm text-text-secondary italic mb-10 border border-surface" data-id="element-425">
        Ask about duration. Ask about recurrence. The story may change the next
        step.
      </div>

      <div className="border-t border-surface pt-8 mt-auto" data-id="element-426">
        <div className="flex items-center justify-center gap-3 mb-4" data-id="element-427">
          <span className="text-lg font-bold tracking-tight text-text-primary flex items-center gap-1" data-id="element-428">
            Anzupgo<sup className="text-[10px] mt-1" data-id="element-429">®</sup>
          </span>
          <span className="text-surface" data-id="element-430">|</span>
          <span className="text-sm font-bold tracking-[0.2em] text-text-secondary" data-id="element-431">
            LEO
          </span>
        </div>
        <p className="text-[10px] leading-relaxed text-text-secondary/60" data-id="element-432">
          For healthcare professionals only. This material is educational and
          not intended to guide diagnosis or prescribing. Refer to the approved
          product information. Prescribing information and references available
          on request.
        </p>
      </div>
    </motion.div>;
}